import React, { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import { listingService } from '../../api/services/ListingService';
import type { SoldListingSummaryViewModel } from '../../api/types/conversation/ListingSummaryViewModel';

export interface BuyerVerifyButtonProps {
  listingId: string;
  currentUserId: string | undefined;
  soldListingSummary: SoldListingSummaryViewModel | null | undefined;
  onVerified: (updated: SoldListingSummaryViewModel) => void;
  className?: string;
}

/**
 * Renders the "Satın aldığımı onayla" CTA for the buyer during the two-phase
 * sale flow. Only visible when the current user is the buyer and the sale is
 * not yet buyer-verified. See specs/008-messaging-context-ratings §2.
 */
const BuyerVerifyButton: React.FC<BuyerVerifyButtonProps> = ({
  listingId,
  currentUserId,
  soldListingSummary,
  onVerified,
  className,
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isVisible = useMemo(() => {
    if (!soldListingSummary || !currentUserId) return false;
    if (soldListingSummary.soldToUserId !== currentUserId) return false;
    return soldListingSummary.buyerVerifiedAt == null;
  }, [soldListingSummary, currentUserId]);

  const handleConfirm = useCallback(async () => {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const result = await listingService.buyerVerify(listingId);
      if (!result.isSuccess || !result.data) {
        setErrorMessage(result.message ?? 'Onaylanamadı. Lütfen tekrar deneyin.');
        return;
      }
      onVerified({
        id: result.data.id,
        soldToUserId: result.data.soldToUserId,
        sellerVerifiedAt: result.data.sellerVerifiedAt,
        buyerVerifiedAt: result.data.buyerVerifiedAt ?? null,
        completedAt: result.data.completedAt ?? null,
        hasRating: false,
      });
      setIsConfirmOpen(false);
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ?? err.message
        : 'Beklenmedik bir hata oluştu.';
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  }, [listingId, onVerified]);

  if (!isVisible) return null;

  return (
    <>
      <button
        type="button"
        className={className ?? 'btn btn-outline-success'}
        onClick={() => {
          setErrorMessage(null);
          setIsConfirmOpen(true);
        }}
      >
        <i className="fas fa-check me-2" />
        Satın aldığımı onayla
      </button>

      {isConfirmOpen && (
        <>
          <div
            className="modal fade show"
            style={{ display: 'block' }}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Satın aldığımı onayla</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Kapat"
                    onClick={() => !submitting && setIsConfirmOpen(false)}
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-2">
                    Bu ilanı satın aldığınızı onaylıyor musunuz? Onay sonrası 24 saat
                    içinde satıcıyı değerlendirmeniz istenecek.
                  </p>
                  {errorMessage && (
                    <div className="alert alert-danger py-2 mb-0" role="alert">
                      {errorMessage}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setIsConfirmOpen(false)}
                    disabled={submitting}
                  >
                    Vazgeç
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleConfirm}
                    disabled={submitting}
                  >
                    {submitting ? 'Onaylanıyor…' : 'Evet, onaylıyorum'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </>
  );
};

export default BuyerVerifyButton;
